import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function MyPage() {
  const navigate = useNavigate();

  // 입력 상태
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthHour, setBirthHour] = useState('');
  const [gender, setGender] = useState('');
  const [isLunar, setIsLunar] = useState(false);
  const [phone1, setPhone1] = useState('010');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');

  // 에러 상태
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 편집 모드 상태
  const [isEditing, setIsEditing] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  // 모달 상태
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [showRankModal, setShowRankModal] = useState(false);

  // 구슬 UI 상태
  const [hoveredOrb, setHoveredOrb] = useState(null);
  const [particles, setParticles] = useState([]);

  // 결제 상태
  const [purchaseStatus, setPurchaseStatus] = useState({
    daily: false,
    monthly: false,
    newyear: false,
    lifetime: false
  });

  const activeCount = Object.values(purchaseStatus).filter(v => v).length;

  const orbData = [
    { key: 'daily', name: '일일사주', icon: '☀️', color: '#3b82f6', lightColor: '#dbeafe', angle: -135, desc: '매일 새로운 운세', reward: '일일 운세 무제한' },
    { key: 'lifetime', name: '평생사주', icon: '⭐', color: '#f59e0b', lightColor: '#fef3c7', angle: -45, desc: '평생의 운명을 알다', reward: '평생 사주 분석' },
    { key: 'monthly', name: '월간사주', icon: '🌙', color: '#10b981', lightColor: '#d1fae5', angle: 135, desc: '이번 달 행운의 흐름', reward: '월간 운세 리포트' },
    { key: 'newyear', name: '신년운세', icon: '🎆', color: '#ef4444', lightColor: '#fee2e2', angle: 45, desc: '2025년 대운을 확인', reward: '2025 신년 특별 운세' }
  ];

  // 등급 데이터
  const rankData = [
    { count: 0, title: '입문자', emoji: '🌱', color: '#9ca3af', desc: '운명의 여정을 시작하세요' },
    { count: 1, title: '수련생', emoji: '🔮', color: '#3b82f6', desc: '첫 번째 구슬을 획득했어요' },
    { count: 2, title: '탐험가', emoji: '⚡', color: '#8b5cf6', desc: '운명을 탐험하는 중이에요' },
    { count: 3, title: '현자', emoji: '🌟', color: '#f59e0b', desc: '깊은 통찰력을 얻었어요' },
    { count: 4, title: '운명의 주인', emoji: '👑', color: '#ef4444', desc: '모든 운명을 손에 넣었어요!' }
  ];

  const getCurrentRank = () => rankData.find(r => r.count === activeCount) || rankData[0];
  const rank = getCurrentRank();
  const progressPercent = (activeCount / 4) * 100;

  // 파티클 생성
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeCount > 0) {
        setParticles(prev => [...prev.slice(-20), {
          id: Date.now(),
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 3 + 2,
          duration: Math.random() * 3 + 2
        }]);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [activeCount]);

  // 페이지 로드 시 기존 정보 불러오기
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const backendUrl = 'https://ownwan-backend.onrender.com';
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${backendUrl}/api/profile`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();

          if (data.birth) {
            setBirthYear(data.birth.year.toString());
            setBirthMonth(data.birth.month.toString());
            setBirthDay(data.birth.day.toString());
            setBirthHour(data.birth.hour.toString());
            setGender(data.gender || '');
            setIsLunar(data.isLunar || false);

            if (data.phone) {
              const phoneParts = data.phone.split('-');
              if (phoneParts.length === 3) {
                setPhone1(phoneParts[0]);
                setPhone2(phoneParts[1]);
                setPhone3(phoneParts[2]);
              }
            }

            setIsEditing(false);
            setIsSaved(true);
          }
        }
      } catch (error) {
        console.error('❌ 사용자 정보 불러오기 실패:', error);
      }
    };

    fetchUserInfo();
  }, []);

  // 결제 상태 불러오기
  useEffect(() => {
    const fetchPurchaseStatus = async () => {
      try {
        const backendUrl = 'https://ownwan-backend.onrender.com';
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${backendUrl}/api/purchase-status`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPurchaseStatus(data);
        }
      } catch (error) {
        console.error('❌ 결제 상태 불러오기 실패:', error);
      }
    };

    fetchPurchaseStatus();
  }, []);

  // 로그아웃 핸들러
  const handleLogout = async () => {
    try {
      const backendUrl = 'https://ownwan-backend.onrender.com';
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log('✅ 로그아웃 성공!');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        console.error('❌ 로그아웃 실패');
        localStorage.removeItem('access_token');
        navigate('/login');
      }
    } catch (error) {
      console.error('❌ 로그아웃 에러:', error);
      localStorage.removeItem('access_token');
      navigate('/login');
    }
  };

  // 수정하기 버튼 핸들러
  const handleEdit = (e) => {
    e.preventDefault();
    setIsEditing(true);
    setError('');
  };

  // 구슬 클릭 핸들러
  const handleOrbClick = (type) => {
    if (purchaseStatus[type]) {
      const routes = {
        daily: '/daily-result',
        monthly: '/monthly-result',
        newyear: '/newyear-result',
        lifetime: '/lifetime-result'
      };
      navigate(routes[type]);
    } else {
      const routes = {
        daily: '/payment',
        monthly: '/monthly-payment',
        newyear: '/newyear',
        lifetime: '/lifetime'
      };
      navigate(routes[type]);
    }
  };

  // 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!birthYear || !birthMonth || !birthDay) {
      setError('생년월일을 모두 입력해주세요.');
      return;
    }

    if (!birthHour) {
      setError('출생 시간대를 선택해주세요.');
      return;
    }

    if (!gender) {
      setError('성별을 선택해주세요.');
      return;
    }

    if (!phone2 || !phone3) {
      setError('휴대폰번호를 모두 입력해주세요.');
      return;
    }

    const year = parseInt(birthYear);
    const month = parseInt(birthMonth);
    const day = parseInt(birthDay);
    const hour = parseInt(birthHour);
    const minute = 0;

    if (year < 1900 || year > 2024) {
      setError('올바른 출생 연도를 입력해주세요. (1900-2024)');
      return;
    }

    if (month < 1 || month > 12) {
      setError('올바른 월을 입력해주세요. (1-12)');
      return;
    }

    if (day < 1 || day > 31) {
      setError('올바른 일을 입력해주세요. (1-31)');
      return;
    }

    setIsSubmitting(true);

    try {
      const phoneNumber = `${phone1}-${phone2}-${phone3}`;
      const backendUrl = 'https://ownwan-backend.onrender.com';
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/profile/update-birth-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          birth_year: year,
          birth_month: month,
          birth_day: day,
          birth_hour: hour,
          birth_minute: minute,
          gender: gender,
          isLunar: isLunar,
          phone: phoneNumber
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '정보 저장 실패');
      }

      console.log('✅ 생년월일 정보 저장 성공!');
      setIsEditing(false);
      setIsSaved(true);
      setShowSuccessModal(true);

    } catch (error) {
      console.error('❌ 생년월일 정보 저장 에러:', error);
      setError(error.message || '정보 저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 overflow-hidden relative pb-8">
      {/* 커스텀 애니메이션 */}
      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }
        @keyframes goldGlow {
          0%, 100% { box-shadow: 0 0 5px #fbbf24, 0 0 10px #fbbf24, 0 0 15px #f59e0b; }
          50% { box-shadow: 0 0 10px #fbbf24, 0 0 20px #fbbf24, 0 0 30px #f59e0b; }
        }
        @keyframes orbFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-4px) scale(1.02); }
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.03); }
        }
        @keyframes floatUp {
          0% { opacity: 0.6; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-80px) scale(0); }
        }
      `}</style>

      {/* 육각형 패턴 배경 */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="hex-mypage" width="50" height="43.4" patternUnits="userSpaceOnUse">
              <polygon points="25,0 50,12.5 50,37.5 25,50 0,37.5 0,12.5" fill="none" stroke="#000" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hex-mypage)"/>
        </svg>
      </div>

      {/* 플로팅 파티클 */}
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
            borderRadius: '50%',
            animation: `floatUp ${p.duration}s ease-out forwards`,
            pointerEvents: 'none',
            opacity: 0.6
          }}
        />
      ))}

      {/* ===== 등급 시스템 모달 ===== */}
      {showRankModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowRankModal(false)}
        >
          <div 
            className="bg-white rounded-3xl p-6 max-w-sm w-full border-2 border-gray-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <h3 className="text-lg font-black text-gray-900">🏆 등급 시스템</h3>
              <p className="text-gray-500 text-xs mt-1">구슬을 모아 등급을 올려보세요!</p>
            </div>

            <div className="space-y-2">
              {rankData.map((r, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    r.count === activeCount 
                      ? 'border-gray-900 bg-gray-50 shadow-md' 
                      : r.count < activeCount 
                        ? 'border-gray-200 bg-white opacity-60'
                        : 'border-gray-200 bg-white'
                  }`}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                    style={{ 
                      backgroundColor: r.count <= activeCount ? `${r.color}20` : '#f3f4f6',
                      border: `2px solid ${r.count <= activeCount ? r.color : '#e5e7eb'}`
                    }}
                  >
                    {r.emoji}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span 
                        className="font-bold text-sm"
                        style={{ color: r.count <= activeCount ? r.color : '#9ca3af' }}
                      >
                        {r.title}
                      </span>
                      {r.count === activeCount && (
                        <span className="bg-amber-400 text-gray-900 text-xs font-black px-2 py-0.5 rounded">현재</span>
                      )}
                      {r.count < activeCount && (
                        <span className="text-green-500 text-xs font-bold">✓ 달성</span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs">{r.desc}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold" style={{ color: r.count <= activeCount ? r.color : '#d1d5db' }}>
                      {r.count}/4
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowRankModal(false)}
              className="w-full mt-4 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm"
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 성공 모달 */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowSuccessModal(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-3xl border-2 border-gray-900 shadow-2xl max-w-sm w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-10">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center">
                  <span className="text-3xl">✅</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                저장 완료!
              </h3>
              <p className="text-gray-700 text-center mb-6">
                정보가 성공적으로 저장되었습니다.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full py-3 bg-gray-900 text-white font-bold rounded-xl"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-md mx-auto p-4 space-y-4">
        
        {/* ===== 상단 로고 ===== */}
        <div className="text-center pt-2">
          <div 
            className="inline-block relative"
            style={{ animation: 'wiggle 2s ease-in-out infinite' }}
          >
            <div 
              className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 rounded-2xl"
              style={{ animation: 'goldGlow 2s ease-in-out infinite' }}
            ></div>
            <div className="relative bg-gradient-to-b from-gray-50 to-white rounded-2xl px-5 py-2 border-2 border-gray-900">
              <div className="flex items-center gap-2">
                <span className="text-xl">📬</span>
                <span className="text-gray-900 text-xl font-black">오운완</span>
                <span className="text-base">✨</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== 마이페이지 헤더 카드 ===== */}
        <div className="bg-white rounded-3xl overflow-hidden border-2 border-gray-900 shadow-2xl">
          <div className="bg-gray-900 px-4 py-3 text-center">
            <div className="flex items-center justify-center gap-2">
              <span className="text-base">👤</span>
              <span className="text-white font-black text-lg">마이페이지</span>
            </div>
            <p className="text-gray-400 text-xs mt-0.5">MY PAGE</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-gray-600 text-xs">
              정확한 사주 분석을 위해<br />생년월일과 출생 시간을 입력해주세요
            </p>
          </div>
        </div>

        {/* ===== 나의 사주 컬렉션 (구슬 UI) ===== */}
        <div className="bg-white rounded-3xl overflow-hidden border-2 border-gray-900 shadow-2xl">
          <div 
            className="bg-gray-900 px-4 py-3 cursor-pointer hover:bg-gray-800 transition-all"
            onClick={() => setShowRankModal(true)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{rank.emoji}</span>
                <div>
                  <div className="text-white font-black text-base">{rank.title}</div>
                  <div className="text-gray-400 text-xs">👆 등급 보기</div>
                </div>
              </div>
              <div className="bg-amber-400 text-gray-900 text-sm font-black px-3 py-1.5 rounded-lg">
                {activeCount}/4 수집
              </div>
            </div>
            
            {/* 프로그레스 바 */}
            <div className="mt-3">
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-300 rounded-full"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="text-center mb-4">
              <h2 className="text-sm font-black text-gray-900 flex items-center justify-center gap-1">
                <span className="text-amber-500">✧</span>
                운명의 구슬
                <span className="text-amber-500">✧</span>
              </h2>
              <p className="text-gray-400 text-xs">구슬을 클릭하여 운명을 해금하세요</p>
            </div>

            {/* 구슬 UI */}
            <div className="relative mx-auto" style={{ width: '220px', height: '220px' }}>
              
              {/* 외곽 회전 링 */}
              <div
                style={{
                  position: 'absolute',
                  inset: '0px',
                  border: '2px dashed rgba(139,92,246,0.3)',
                  borderRadius: '50%',
                  animation: 'rotate 25s linear infinite'
                }}
              />

              {/* 중앙 코어 - 클릭하면 등급 모달 */}
              <div
                onClick={() => setShowRankModal(true)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: `conic-gradient(
                    from 0deg,
                    ${purchaseStatus.daily ? '#3b82f6' : '#e5e7eb'} 0deg 90deg,
                    ${purchaseStatus.lifetime ? '#f59e0b' : '#e5e7eb'} 90deg 180deg,
                    ${purchaseStatus.newyear ? '#ef4444' : '#e5e7eb'} 180deg 270deg,
                    ${purchaseStatus.monthly ? '#10b981' : '#e5e7eb'} 270deg 360deg
                  )`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #1f2937',
                  cursor: 'pointer',
                  animation: 'pulse 3s ease-in-out infinite'
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    border: '2px solid #e5e7eb'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{rank.emoji}</span>
                  <span style={{ fontSize: '8px', fontWeight: 'bold', color: rank.color }}>
                    등급보기
                  </span>
                </div>
              </div>

              {/* 4개의 구슬 */}
              {orbData.map((orb, i) => {
                const angle = (orb.angle * Math.PI) / 180;
                const x = 110 + Math.cos(angle) * 80 - 26;
                const y = 110 + Math.sin(angle) * 80 - 26;
                const isActive = purchaseStatus[orb.key];

                return (
                  <div
                    key={orb.key}
                    onMouseEnter={() => setHoveredOrb(orb.key)}
                    onMouseLeave={() => setHoveredOrb(null)}
                    onClick={() => handleOrbClick(orb.key)}
                    style={{
                      position: 'absolute',
                      left: `${x}px`,
                      top: `${y}px`,
                      width: '52px',
                      height: '52px',
                      borderRadius: '50%',
                      background: isActive
                        ? `linear-gradient(135deg, ${orb.lightColor} 0%, white 50%, ${orb.lightColor} 100%)`
                        : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      transform: hoveredOrb === orb.key ? 'scale(1.15)' : 'scale(1)',
                      boxShadow: isActive
                        ? `0 4px 20px ${orb.color}50`
                        : '0 4px 15px rgba(0,0,0,0.08)',
                      border: isActive ? `3px solid ${orb.color}` : '3px solid #d1d5db',
                      animation: isActive ? `orbFloat ${3 + i * 0.3}s ease-in-out infinite` : 'none'
                    }}
                  >
                    <span style={{
                      fontSize: '18px',
                      filter: isActive ? 'none' : 'grayscale(100%) opacity(0.4)'
                    }}>
                      {isActive ? orb.icon : '🔒'}
                    </span>
                    <span style={{
                      fontSize: '7px',
                      fontWeight: 'bold',
                      color: isActive ? orb.color : '#9ca3af',
                      marginTop: '1px'
                    }}>
                      {orb.name}
                    </span>

                    {/* 활성화 체크 */}
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-2px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: orb.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '9px',
                        color: '#fff',
                        border: '2px solid white'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 범례 */}
            <div className="flex justify-center gap-4 mt-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-blue-500"></div>
                <span className="text-gray-500">✓ 해금됨</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-300 flex items-center justify-center text-[6px]">🔒</div>
                <span className="text-gray-400">잠김</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== 입력 폼 카드 ===== */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl overflow-hidden border-2 border-gray-900 shadow-2xl">
          <div className="bg-gray-900 px-4 py-3">
            <div className="flex items-center justify-center gap-2">
              <span className="text-base">📝</span>
              <span className="text-white font-black text-base">사주 정보 입력</span>
            </div>
          </div>

          <div className="p-5 space-y-4">
            
            {/* 생년월일 */}
            <div>
              <label className="text-gray-900 font-bold text-sm flex items-center gap-2 mb-2">
                <span className="w-1 h-4 bg-gray-900 rounded"></span>🎂 생년월일
              </label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  disabled={!isEditing}
                  className={`p-3 border-2 border-gray-200 rounded-xl text-gray-700 text-sm text-center font-bold ${!isEditing ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'}`}
                >
                  <option value="">년</option>
                  {Array.from({ length: 125 }, (_, i) => 2024 - i).map(year => (
                    <option key={year} value={year}>{year}년</option>
                  ))}
                </select>
                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  disabled={!isEditing}
                  className={`p-3 border-2 border-gray-200 rounded-xl text-gray-700 text-sm text-center font-bold ${!isEditing ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'}`}
                >
                  <option value="">월</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>{month}월</option>
                  ))}
                </select>
                <select
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  disabled={!isEditing}
                  className={`p-3 border-2 border-gray-200 rounded-xl text-gray-700 text-sm text-center font-bold ${!isEditing ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'}`}
                >
                  <option value="">일</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}일</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 출생 시간대 */}
            <div>
              <label className="text-gray-900 font-bold text-sm flex items-center gap-2 mb-2">
                <span className="w-1 h-4 bg-gray-900 rounded"></span>⏰ 출생 시간대
              </label>
              <select
                value={birthHour}
                onChange={(e) => setBirthHour(e.target.value)}
                disabled={!isEditing}
                className={`w-full p-3 border-2 border-gray-200 rounded-xl text-gray-700 text-sm font-bold ${!isEditing ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'}`}
              >
                <option value="">시간대를 선택하세요</option>
                <option value="0">자시 子時 (23-01시)</option>
                <option value="2">축시 丑時 (01-03시)</option>
                <option value="4">인시 寅時 (03-05시)</option>
                <option value="6">묘시 卯時 (05-07시)</option>
                <option value="8">진시 辰時 (07-09시)</option>
                <option value="10">사시 巳時 (09-11시)</option>
                <option value="12">오시 午時 (11-13시)</option>
                <option value="14">미시 未時 (13-15시)</option>
                <option value="16">신시 申時 (15-17시)</option>
                <option value="18">유시 酉時 (17-19시)</option>
                <option value="20">술시 戌時 (19-21시)</option>
                <option value="22">해시 亥時 (21-23시)</option>
              </select>
            </div>

            {/* 성별 & 양력/음력 - 메인페이지 스타일 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-900 font-bold text-sm flex items-center gap-2 mb-2">
                  <span className="w-1 h-4 bg-gray-900 rounded"></span>성별
                </label>
                <div className="grid grid-cols-2 gap-1">
                  <button 
                    type="button"
                    onClick={() => isEditing && setGender('남자')}
                    disabled={!isEditing}
                    className={`p-3 rounded-xl font-bold text-sm ${gender === '남자' ? 'bg-gray-900 text-white' : !isEditing ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-50 text-gray-400 border-2 border-gray-200'}`}
                  >
                    남
                  </button>
                  <button 
                    type="button"
                    onClick={() => isEditing && setGender('여자')}
                    disabled={!isEditing}
                    className={`p-3 rounded-xl font-bold text-sm ${gender === '여자' ? 'bg-gray-900 text-white' : !isEditing ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-50 text-gray-400 border-2 border-gray-200'}`}
                  >
                    여
                  </button>
                </div>
              </div>
              <div>
                <label className="text-gray-900 font-bold text-sm flex items-center gap-2 mb-2">
                  <span className="w-1 h-4 bg-gray-900 rounded"></span>양/음력
                </label>
                <div className="grid grid-cols-2 gap-1">
                  <button 
                    type="button"
                    onClick={() => isEditing && setIsLunar(false)}
                    disabled={!isEditing}
                    className={`p-3 rounded-xl font-bold text-sm ${!isLunar ? 'bg-gray-900 text-white' : !isEditing ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-50 text-gray-400 border-2 border-gray-200'}`}
                  >
                    양력
                  </button>
                  <button 
                    type="button"
                    onClick={() => isEditing && setIsLunar(true)}
                    disabled={!isEditing}
                    className={`p-3 rounded-xl font-bold text-sm ${isLunar ? 'bg-gray-900 text-white' : !isEditing ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-50 text-gray-400 border-2 border-gray-200'}`}
                  >
                    음력
                  </button>
                </div>
              </div>
            </div>

            {/* 휴대폰번호 */}
            <div>
              <label className="text-gray-900 font-bold text-sm flex items-center gap-2 mb-2">
                <span className="w-1 h-4 bg-gray-900 rounded"></span>📱 휴대폰번호
              </label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={phone1}
                  onChange={(e) => setPhone1(e.target.value)}
                  disabled={!isEditing}
                  className={`p-3 border-2 border-gray-200 rounded-xl text-gray-700 text-sm text-center font-bold ${!isEditing ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'}`}
                  maxLength="3"
                />
                <input
                  type="number"
                  value={phone2}
                  onChange={(e) => setPhone2(e.target.value)}
                  placeholder="1234"
                  disabled={!isEditing}
                  className={`p-3 border-2 border-gray-200 rounded-xl text-gray-700 text-sm text-center font-bold ${!isEditing ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'}`}
                  maxLength="4"
                />
                <input
                  type="number"
                  value={phone3}
                  onChange={(e) => setPhone3(e.target.value)}
                  placeholder="5678"
                  disabled={!isEditing}
                  className={`p-3 border-2 border-gray-200 rounded-xl text-gray-700 text-sm text-center font-bold ${!isEditing ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'}`}
                  maxLength="4"
                />
              </div>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="bg-red-50 border-2 border-red-500 rounded-xl p-3">
                <p className="text-red-700 text-center font-bold text-xs">{error}</p>
              </div>
            )}

            {/* 버튼 */}
            {isEditing ? (
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-xl font-black text-base text-white ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'}`}
              >
                {isSubmitting ? '저장 중...' : '정보 저장하기'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleEdit}
                className="w-full py-4 rounded-xl font-black text-base text-white bg-blue-600 hover:bg-blue-700"
              >
                수정하기
              </button>
            )}

            {/* 안내 문구 */}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
              <p className="text-gray-500 text-xs text-center">
                💡 출생 시간을 정확히 모르시나요?<br/>대략적인 시간대만 선택해도 괜찮아요!
              </p>
            </div>
          </div>
        </form>

        {/* ===== 고객센터 카드 ===== */}
        <div className="bg-white rounded-3xl p-4 border-2 border-gray-200 shadow-xl">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-300"></div>
            <span className="text-gray-900 text-xs font-black">📞 고객센터</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-300"></div>
          </div>
          
          <div className="space-y-2">
            <button onClick={() => setActiveModal('contact')} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100">
              <div className="flex items-center gap-2">
                <span>✉️</span>
                <span className="text-gray-800 font-medium text-sm">문의하기</span>
              </div>
              <span className="text-gray-400">→</span>
            </button>
            <button onClick={() => setActiveModal('refund')} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100">
              <div className="flex items-center gap-2">
                <span>📋</span>
                <span className="text-gray-800 font-medium text-sm">환불정책</span>
              </div>
              <span className="text-gray-400">→</span>
            </button>
            <button onClick={() => setActiveModal('terms')} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100">
              <div className="flex items-center gap-2">
                <span>📄</span>
                <span className="text-gray-800 font-medium text-sm">이용약관</span>
              </div>
              <span className="text-gray-400">→</span>
            </button>
            <button onClick={() => setActiveModal('privacy')} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100">
              <div className="flex items-center gap-2">
                <span>🔒</span>
                <span className="text-gray-800 font-medium text-sm">개인정보처리방침</span>
              </div>
              <span className="text-gray-400">→</span>
            </button>
          </div>
        </div>

        {/* ===== 로그아웃 버튼 ===== */}
        <button
          onClick={handleLogout}
          className="w-full py-3 bg-red-500 text-white rounded-xl font-bold text-sm border-2 border-red-700 hover:bg-red-600"
        >
          로그아웃
        </button>

        {/* ===== 푸터 ===== */}
        <div className="text-center pt-2">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="text-xs">☯️</span>
            <span className="text-gray-600 font-bold text-xs">오운완</span>
            <span className="text-xs">☯️</span>
          </div>
          <p className="text-gray-400 text-xs">© 2025 OWNWAN. All Rights Reserved.</p>
        </div>

      </div>

      {/* ===== 고객센터 모달들 ===== */}
      {activeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setActiveModal(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md shadow-xl flex flex-col border-2 border-gray-900" style={{ maxHeight: '85vh' }}>

            {/* 문의하기 모달 */}
            {activeModal === 'contact' && (
              <>
                <div className="flex-shrink-0 bg-gray-900 p-4 flex items-center justify-between rounded-t-3xl">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">✉️ 문의하기</h2>
                  <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded-full text-2xl">&times;</button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                  <div className="space-y-4">
                    <p className="text-gray-700 text-sm">문의사항이 있으시면 아래 이메일로 연락해 주세요.</p>
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <p className="text-gray-500 text-xs mb-1">이메일</p>
                      <p className="text-gray-900 font-medium">chol5622729@naver.com</p>
                    </div>
                    <a href="mailto:chol5622729@naver.com?subject=[오운완 문의]" className="block w-full py-3 bg-gray-900 text-white font-bold rounded-xl text-center">
                      이메일 보내기
                    </a>
                    <p className="text-xs text-gray-500 text-center">답변은 영업일 기준 1~2일 이내에 드립니다.</p>
                  </div>
                </div>
              </>
            )}

            {/* 환불정책 모달 */}
            {activeModal === 'refund' && (
              <>
                <div className="flex-shrink-0 bg-gray-900 p-4 flex items-center justify-between rounded-t-3xl">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">📋 환불정책</h2>
                  <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded-full text-2xl">&times;</button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4 text-gray-700 text-sm leading-relaxed">
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제1조 (환불 원칙)</h3>
                    <p>오운완 서비스는 「전자상거래 등에서의 소비자보호에 관한 법률」에 따라 환불 정책을 운영합니다.</p>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제2조 (환불 가능 기간)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li><strong>월간 구독권:</strong> 결제일로부터 7일 이내, 서비스 미이용 시 전액 환불</li>
                      <li><strong>단건 상품:</strong> 결제일로부터 7일 이내, 콘텐츠 미열람 시 전액 환불</li>
                      <li><strong>평생 이용권:</strong> 결제일로부터 7일 이내, 서비스 미이용 시 전액 환불</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제3조 (환불 불가 사유)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>콘텐츠를 이미 열람한 경우</li>
                      <li>구독 기간 중 일부를 사용한 경우</li>
                      <li>결제일로부터 7일이 경과한 경우</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제4조 (환불 신청 방법)</h3>
                    <p>고객센터 문의하기를 통해 회원 이메일, 결제일자, 환불 사유를 알려주세요.</p>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제5조 (환불 처리 기간)</h3>
                    <p>영업일 기준 3~7일 이내에 처리됩니다.</p>
                  </section>
                  <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
                    <p>시행일: 2025년 1월 1일</p>
                    <p>문의: chol5622729@naver.com</p>
                  </div>
                </div>
              </>
            )}

            {/* 이용약관 모달 */}
            {activeModal === 'terms' && (
              <>
                <div className="flex-shrink-0 bg-gray-900 p-4 flex items-center justify-between rounded-t-3xl">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">📄 이용약관</h2>
                  <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded-full text-2xl">&times;</button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4 text-gray-700 text-sm leading-relaxed">
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제1조 (목적)</h3>
                    <p>본 약관은 오운완(이하 "서비스")이 제공하는 운세 정보 서비스의 이용 조건을 규정합니다.</p>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제2조 (서비스의 내용)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>AI 기반 개인 맞춤형 운세 정보 제공</li>
                      <li>일간/월간 운세 리포트 서비스</li>
                      <li>사주 분석 및 해석 서비스</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제3조 (회원가입 및 자격)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>카카오톡 또는 네이버 소셜 로그인으로 가입</li>
                      <li>만 14세 미만은 서비스 이용 제한</li>
                      <li>허위 정보 입력 시 이용 제한</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제4조 (면책조항)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>본 서비스는 오락 및 참고 목적으로 제공됩니다.</li>
                      <li>중요한 결정에 대한 전문적 조언을 대체하지 않습니다.</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제5조 (저작권)</h3>
                    <p>모든 콘텐츠의 저작권은 오운완에 있으며, 무단 복제를 금합니다.</p>
                  </section>
                  <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
                    <p>시행일: 2025년 1월 1일</p>
                    <p>문의: chol5622729@naver.com</p>
                  </div>
                </div>
              </>
            )}

            {/* 개인정보처리방침 모달 */}
            {activeModal === 'privacy' && (
              <>
                <div className="flex-shrink-0 bg-gray-900 p-4 flex items-center justify-between rounded-t-3xl">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">🔒 개인정보처리방침</h2>
                  <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded-full text-2xl">&times;</button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4 text-gray-700 text-sm leading-relaxed">
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제1조 (수집하는 개인정보)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li><strong>필수:</strong> 이름(닉네임), 이메일, 생년월일, 성별</li>
                      <li><strong>자동 수집:</strong> 서비스 이용기록, 접속 로그</li>
                      <li><strong>결제 시:</strong> 결제 정보 (결제대행사 처리)</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제2조 (수집 목적)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>맞춤형 운세 서비스 제공</li>
                      <li>회원 관리 및 본인 확인</li>
                      <li>유료 서비스 결제 처리</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제3조 (보유 기간)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>회원 탈퇴 시 즉시 파기</li>
                      <li>전자상거래법에 따른 거래기록: 5년</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제4조 (제3자 제공)</h3>
                    <p>원칙적으로 외부에 제공하지 않습니다. 단, 이용자 동의 또는 법령에 의한 경우 예외.</p>
                  </section>
                  <section>
                    <h3 className="font-bold text-gray-900 mb-2">제5조 (이용자의 권리)</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>개인정보 열람, 수정, 삭제 요청</li>
                      <li>회원 탈퇴 요청</li>
                    </ul>
                  </section>
                  <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
                    <p>시행일: 2025년 1월 1일</p>
                    <p>개인정보 보호책임자: chol5622729@naver.com</p>
                  </div>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
